// Third party
import { format } from 'date-fns';
import dayjs, { Dayjs } from 'dayjs';

// Components
import { TBillingReport } from '../types';
import { EnrichedSession, TUserMode } from '../types/models/userSession';

const fmtMs = (ms: number) => {
  if (!ms || ms <= 0) return '-';
  const totalMin = Math.floor(ms / 60000);
  const h = Math.floor(totalMin / 60);
  const m = totalMin % 60;
  if (h === 0) return `${m}m`;
  if (m === 0) return `${h}h`;
  return `${h}h ${m}m`;
};

const buildUsersCsvRows = (users: any) => {
  const rows: string[][] = [];
  rows.push(['User', 'Sessions', 'Session Time', 'Training Time']);

  users.forEach((u: any) => {
    rows.push([
      u.username ?? 'Unknown',
      String(u.sessions ?? 0),
      fmtMs(u.totalSessionTime ?? 0),
      fmtMs(u.totalTrainingTime ?? 0),
    ]);
  });

  return rows;
}

const exportUsersCsv = (users: TUserMode[], selectedDate: Dayjs | null) => {
  const month = format(selectedDate?.toDate?.() ?? new Date(), 'yyyy-MM');
  exportCsv(buildUsersCsvRows(users), `users-${month}.csv`);
}

const exportCsv = (rows: string[][], filename: string) => {
  if (!rows || rows.length === 0) return;

  const csvContent = rows
    .map((r) => r.map((cell) => `"${(cell ?? '').toString().replace(/"/g, '""')}"`).join(',')) // escape quotes
    .join('\r\n');

  const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
};

const monthKeyOf = (selectedDate: Dayjs | null) =>
  format(selectedDate?.toDate?.() ?? new Date(), 'yyyy-MM');

export function buildUsageCsvRows(sessions: EnrichedSession[]) {
  const rows: string[][] = [];
  rows.push(['Status', 'User', 'Session Time', 'Training Time', 'Start Time']);

  sessions.forEach((session) => {
    let status = 'Ended';
    if (session.serial === 'Ungrouped') status = 'Ungrouped';
    if (session.end_time === null) status = 'Active';
    if (session.end_time === '1969-12-31T23:59:59') status = 'Error';

    const startMs = new Date(session.start_time).getTime();
    const endMs = session.end_time ? new Date(session.end_time).getTime() : Date.now();
    let sessionTime = '-';
    if (endMs > startMs) {
      const totalMin = Math.floor((endMs - startMs) / 60000);
      const h = Math.floor(totalMin / 60);
      const m = totalMin % 60;
      sessionTime = `${h}h ${m}m`;
    }

    let trainingMin = 0;
    session.transactions.forEach((txn) => {
      if (txn.created_timestamp && txn.finished_timestamp) {
        const s = new Date(txn.created_timestamp).getTime();
        const e = new Date(txn.finished_timestamp).getTime();
        if (e > s) trainingMin += Math.floor((e - s) / 60000);
      }
    });
    const trainingTime =
      trainingMin > 0 ? `${Math.floor(trainingMin / 60)}h ${trainingMin % 60}m` : '-';

    const startTime = session.start_time
      ? format(new Date(session.start_time), 'yyyy-MM-dd HH:mm')
      : 'N/A';

    rows.push([status, session.username, sessionTime, trainingTime, startTime]);
  });

  return rows;
}

export function exportUsageCsv(sessions: EnrichedSession[], selectedDate: Dayjs | null) {
  exportCsv(buildUsageCsvRows(sessions), `report-${monthKeyOf(selectedDate)}.csv`);
}

const fmtMins = (mins: number) => {
  if (!mins || mins <= 0) return '0m';
  return `${Math.ceil(mins)}m`;
};

export function buildBillingCsvRowsByUser(rep: TBillingReport) {
  const rows: string[][] = [];
  rows.push(['User', 'Scenarios', 'Training Time']);

  if (!rep || !Array.isArray(rep.session_details)) {
    return rows;
  }

  const userRows = rep.session_details
    .map((user) => {
      let trainingTime = 0;

      user.sessions.forEach((s) => {
        trainingTime += Math.ceil(s.payable_time ?? 0);
      });

      return {
        user_id: user.user_id,
        username: user.username,
        scenarios: user.sessions.length,
        trainingTime,
      };
    })
    .filter(
      (u) =>
        u.scenarios > 0 ||
        u.trainingTime > 0
    );

  userRows.forEach((u) => {
    rows.push([
      u.username ?? String(u.user_id),
      String(u.scenarios),
      fmtMins(u.trainingTime),
    ]);
  });

  const totals = userRows.reduce(
    (acc, r) => {
      acc.scenarios += r.scenarios;
      acc.trainingTime += r.trainingTime;
      return acc;
    },
    {
      scenarios: 0,
      trainingTime: 0,
      idleTime: 0,
      totalTime: 0,
    }
  );

  rows.push([
    'Total',
    String(totals.scenarios),
    fmtMins(totals.trainingTime),
  ]);

  return rows;
}

const calcDailySessionStats = (report: TBillingReport | null) => {
  if (!report) {
    return {
      rows: [],
      totals: { scenarios: 0, trainingTime: 0, idleTime: 0, totalTime: 0 },
    };
  }

  const byDay: Record<string, any> = {};

  report.session_details.forEach((user) => {
    user.sessions?.forEach((s) => {
      const dayKey = dayjs(s.start_time).format('YYYY-MM-DD');

      if (!byDay[dayKey]) {
        byDay[dayKey] = {
          day: dayKey,
          scenarios: 0,
          trainingTime: 0,
          totalTime: 0,
        };
      }

      byDay[dayKey].scenarios += s.scenario_count ?? 1;
      byDay[dayKey].trainingTime += Math.ceil(s.payable_time ?? 0);

      const diff = dayjs(s.end_time).diff(dayjs(s.start_time), 'minute', true);
      byDay[dayKey].totalTime += Math.ceil(diff);
    });
  });

  const rows = Object.values(byDay).map((d) => ({
    ...d,
    idleTime: Math.max(0, d.totalTime - d.trainingTime),
    day: dayjs(d.day).format('DD.MM.YYYY'),
  }));

  const totals = rows.reduce(
    (a, d) => ({
      scenarios: a.scenarios + d.scenarios,
      trainingTime: a.trainingTime + d.trainingTime,
      idleTime: a.idleTime + d.idleTime,
      totalTime: a.totalTime + d.totalTime,
    }),
    { scenarios: 0, trainingTime: 0, idleTime: 0, totalTime: 0 }
  );

  return { rows, totals };
};

const buildBillingCsvRowsByDay = (rep: TBillingReport) => {
  const { rows, totals } = calcDailySessionStats(rep);

  const csv: string[][] = [];
  csv.push(['Day', 'Scenarios', 'Training Time']);

  rows.forEach((d) => {
    csv.push([
      d.day.replace(/\./g, '-'),
      String(d.scenarios),
      `${d.trainingTime}m`,
    ]);
  });

  csv.push([
    'Total',
    String(totals.scenarios),
    `${totals.trainingTime}m`,
  ]);

  return csv;
};

const exportBillingCsv = ({ report, viewType }: { report: TBillingReport; viewType: 'User' | 'Day' }) => {
  const filename = viewType === 'Day'
    ? 'billing-by-day.csv'
    : 'billing-by-user.csv';

  exportCsv(
    viewType === 'Day'
      ? buildBillingCsvRowsByDay(report)
      : buildBillingCsvRowsByUser(report),
    filename
  );
};

export { exportBillingCsv, calcDailySessionStats, exportUsersCsv };